import { CloseOutlined, DeleteOutlined, EditOutlined } from "@ant-design/icons";
import { Button, Empty, Flex, Spin, Table, Tag, Typography } from "antd";
import { useAuthFetch } from "components/Login/hook";
import { useCallback, useEffect, useMemo, useState } from "react";

type McpRoleTool = { server: string; tool: string };

type McpBindingUser =
  | { type: "any" }
  | { type: "group"; value: string }
  | { type: "user"; value: string };
type McpBindingAgent =
  | { type: "any" }
  | { type: "azp"; value: string }
  | { type: "platform"; value: string };
type McpRoleBinding = {
  user: McpBindingUser;
  agent: McpBindingAgent;
  expiresAt: number;
  requestId: string;
};

const roleColumns = [
  { title: "Role", dataIndex: "roleName", key: "roleName" },
  {
    title: "Tools",
    dataIndex: "tools",
    key: "tools",
    render: (tools: McpRoleTool[]) =>
      tools.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No tools" />
      ) : (
        tools.map((t) => (
          <Tag key={`${t.server}/${t.tool}`}>
            {t.server}/{t.tool}
          </Tag>
        ))
      ),
  },
  {
    title: "Actions",
    key: "actions",
    render: () => (
      <Flex justify="flex-end">
        <Button icon={<EditOutlined />} type="text">
          Edit
        </Button>
        <Button icon={<DeleteOutlined />} type="text" danger>
          Remove
        </Button>
      </Flex>
    ),
    width: 200,
  },
];

const McpRolesPanel: React.FC = () => {
  const [roles, setRoles] = useState<Record<string, McpRoleTool[]>>();
  const [loading, setLoading] = useState(true);
  const authFetch = useAuthFetch(undefined, setLoading);

  const fetchRoles = useCallback(async () => {
    const response = await authFetch("mcp-rbac/roles", { method: "GET" });
    if (response?.ok) {
      const data = await response.json();
      setRoles(data.roles);
    }
    setLoading(false);
  }, [authFetch]);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  const dataSource = useMemo(
    () =>
      roles
        ? Object.entries(roles).map(([roleName, tools]) => ({
            roleName,
            tools,
          }))
        : undefined,
    [roles]
  );

  if (loading) return <Spin />;
  if (!dataSource?.length) return <Empty description="No roles defined" />;
  return (
    <Table
      columns={roleColumns}
      dataSource={dataSource}
      rowKey="roleName"
      pagination={false}
      size="small"
    />
  );
};

const bindingColumns = [
  { title: "Role", dataIndex: "roleName", key: "roleName" },
  { title: "User", dataIndex: "user", key: "user" },
  { title: "Agent", dataIndex: "agent", key: "agent" },
  {
    title: "Expires",
    dataIndex: "expiresAt",
    key: "expiresAt",
    render: (ts: number) => new Date(ts).toLocaleString(),
  },
  {
    title: "Actions",
    key: "actions",
    render: () => (
      <Flex justify="flex-end">
        <Button icon={<CloseOutlined />} type="text" danger>
          Revoke
        </Button>
      </Flex>
    ),
    width: 200,
  },
];

const McpRoleBindingsPanel: React.FC = () => {
  const [bindings, setBindings] = useState<Record<string, McpRoleBinding[]>>();
  const [loading, setLoading] = useState(true);
  const authFetch = useAuthFetch(undefined, setLoading);

  const fetchBindings = useCallback(async () => {
    const response = await authFetch("mcp-rbac/bindings", { method: "GET" });
    if (response?.ok) {
      const data = await response.json();
      setBindings(data.bindings);
    }
    setLoading(false);
  }, [authFetch]);

  useEffect(() => {
    fetchBindings();
  }, [fetchBindings]);

  const dataSource = useMemo(() => {
    if (!bindings) return undefined;
    const now = Date.now();
    return Object.entries(bindings).flatMap(([roleName, roleBindings]) =>
      roleBindings
        .filter((b) => b.expiresAt > now)
        .map((b) => ({
          key: b.requestId,
          roleName,
          user:
            b.user.type === "user" || b.user.type === "group"
              ? b.user.value
              : "any",
          agent:
            b.agent.type === "platform" || b.agent.type === "azp"
              ? b.agent.value
              : "any",
          expiresAt: b.expiresAt,
        }))
    );
  }, [bindings]);

  if (loading) return <Spin />;
  if (!dataSource?.length)
    return <Empty description="No active role bindings" />;
  return (
    <Table
      columns={bindingColumns}
      dataSource={dataSource}
      rowKey="key"
      pagination={false}
      size="small"
    />
  );
};

export const McpRbacPage: React.FC = () => (
  <Flex vertical gap={24} style={{ maxWidth: 1000 }}>
    <div>
      <Typography.Title level={4}>Roles</Typography.Title>
      <McpRolesPanel />
    </div>
    <div>
      <Typography.Title level={4}>Role assignments</Typography.Title>
      <McpRoleBindingsPanel />
    </div>
  </Flex>
);
